import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Sparkles, Pause, Play } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Message {
  text: string;
  useDisplayName: boolean;
}

export default function RotatingWelcomeMessage() {
  const [displayName, setDisplayName] = useState<string>("");
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    loadDisplayName();
  }, []);

  useEffect(() => {
    // Only rotate messages if not paused
    if (isPaused) return;

    const interval = setInterval(() => {
      setIsVisible(false);
      setTimeout(() => {
        setCurrentMessageIndex((prev) => (prev + 1) % getMessages().length);
        setIsVisible(true);
      }, 300); // Brief pause for fade transition
    }, 10000); // Rotate every 10 seconds

    return () => clearInterval(interval);
  }, [displayName, isPaused]);

  const loadDisplayName = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) return;

      const { data } = await supabase
        .from("profiles")
        .select("display_name")
        .eq("user_id", user.id)
        .single();

      if (data?.display_name) {
        setDisplayName(data.display_name);
      }
    } catch (error) {
      console.error("Error loading display name:", error);
    }
  };

  const getTimeBasedGreeting = (): string => {
    const hour = new Date().getHours();
    
    if (hour < 12) {
      return displayName ? `Good morning, ${displayName}` : "Good morning";
    } else if (hour < 17) {
      return displayName ? `Good afternoon, ${displayName}` : "Good afternoon";
    } else {
      return displayName ? `Good evening, ${displayName}` : "Good evening";
    }
  };

  const getMessages = (): Message[] => {
    return [
      // Time-based greeting
      { text: getTimeBasedGreeting(), useDisplayName: false },
      
      // Welcome backs
      { text: displayName ? `Welcome back, ${displayName}` : "Welcome back", useDisplayName: false },
      { text: "Great to see you again", useDisplayName: false },
      
      // Genuine check-ins
      { text: "How are you feeling today?", useDisplayName: false },
      { text: "What's one thing you're noticing today?", useDisplayName: false },
      { text: "Taking a moment for yourself?", useDisplayName: false },
      
      // Gentle encouragement (not cheesy)
      { text: "Every day is data", useDisplayName: false },
      { text: "You're learning your body's language", useDisplayName: false },
      { text: "Progress, not perfection", useDisplayName: false },
      { text: "Your journey, your pace", useDisplayName: false },
      { text: "Small steps count", useDisplayName: false },
      
      // Reflection prompts
      { text: "What's different today?", useDisplayName: false },
      { text: "Noticed any patterns lately?", useDisplayName: false },
      { text: "Being kind to yourself today?", useDisplayName: false },
    ];
  };

  const messages = getMessages();
  const currentMessage = messages[currentMessageIndex];

  return (
    <div className="w-full py-20 bg-background">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-6">
            A moment for you
          </p>
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full border border-border/60 bg-card mb-8">
            <Sparkles className="h-5 w-5 text-primary" strokeWidth={1.5} />
          </div>

          <div className="min-h-[80px] flex items-center justify-center">
            <h2
              className={`font-serif text-3xl md:text-4xl leading-tight text-foreground transition-opacity duration-500 ${
                isVisible ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <em className="italic font-normal">{currentMessage.text}</em>
            </h2>
          </div>

          {/* Progress dots and pause button */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <div className="flex items-center gap-1.5">
              {messages.map((_, index) => (
                <div
                  key={index}
                  className={`h-[2px] rounded-full transition-all duration-500 ${
                    index === currentMessageIndex
                      ? 'w-6 bg-primary/70'
                      : 'w-1.5 bg-border'
                  }`}
                />
              ))}
            </div>

            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsPaused(!isPaused)}
              className="h-7 w-7 rounded-full text-muted-foreground hover:text-foreground hover:bg-transparent"
              aria-label={isPaused ? "Resume messages" : "Pause messages"}
            >
              {isPaused ? (
                <Play className="h-3.5 w-3.5" strokeWidth={1.5} />
              ) : (
                <Pause className="h-3.5 w-3.5" strokeWidth={1.5} />
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
